import { CONFIG, surveySettingsDefaults } from './config.js';
import { StorageManager } from './storage/storage-manager.js';
import { generateUUID } from './utils/uuid.js';
import { normalizeCan } from './utils/normalize.js';
import { getUserAttributes, fetchSurveyPreview, readUserAttributesCache, storeUserAttributesCache } from './api/backend.js';
import { initializePH1API, setupPHAPIQueue } from './api/public-api.js';
import { checkURL } from './display/rules.js';
import { applyPurchaseToAttributes, mergeAttributes } from './display/attributes.js';
import { checkShowConditions, getSurveyDisplayDecision, isSurveyActive } from './display/conditions.js';
import { checkShowTiming } from './display/timing.js';
import { SurveyEngine } from './engine/survey-engine.js';
import { launchSurvey } from './engine/survey-launcher.js';
import { fetchAndStoreUTMParameters } from './utils/utm.js';

let storage = null;
let attributes = {};
let loadedProjectConfig = null;
let currentProjectId = null;
let currentIsStandAloneSurvey = false;
let currentIsStandAlonePreview = false;
let currentCan = {};

export function getStorage() {
    return storage;
}

export function getLoadedProjectConfig() {
    return loadedProjectConfig;
}

export function getAttributes() {
    return attributes;
}

export function isStandAlonePreview() {
    return currentIsStandAlonePreview;
}

// The attributes endpoint is only needed when targeting rules use eCommerce or
// user attribute criteria. The config flags this via `needs_user_attributes`;
// only an explicit `false` skips the request, a missing field (older config)
// keeps the previous behaviour. Without a valid user id the request is never
// sent (it would hit `/api/attributes/{project_id}/null`).
export function shouldLoadUserAttributes(needsUserAttributes, userId) {
    if (needsUserAttributes === false) return false;
    if (userId === null || userId === undefined || userId === '') return false;
    return true;
}

function loadUserAttributes(projectId, needsUserAttributes) {
    const userId = storage?.get('ph_user_id');
    if (!shouldLoadUserAttributes(needsUserAttributes, userId)) {
        // No request, but attributes persisted after a purchase on this tab
        // are still picked up (see refreshUrlTriggeredSurveys)
        return Promise.resolve(readUserAttributesCache(projectId, userId) || {});
    }
    return getUserAttributes(CONFIG.apiUrl, projectId, userId);
}

export function loadProjectConfig(projectId) {
    return new Promise((resolve, reject) => {
        if (window.POLLHERO_PROJECT_DATA?.[projectId]) {
            resolve(window.POLLHERO_PROJECT_DATA[projectId]);
            return;
        }

        const configScript = document.createElement('script');
        configScript.src = `${CONFIG.cdnUrl}/configs/${projectId}.js`;
        configScript.async = true;

        configScript.onload = () => {
            if (window.POLLHERO_PROJECT_DATA?.[projectId]) {
                resolve(window.POLLHERO_PROJECT_DATA[projectId]);
            } else {
                reject(new Error('Config script loaded but data not found'));
            }
        };

        configScript.onerror = () => {
            reject(new Error(`Failed to load config for project ${projectId}`));
        };

        document.head.appendChild(configScript);
    });
}

export async function initialize(projectId, surveyIdFromURL, isStandAloneSurvey, registerCustomElement, options = {}) {
    try {
        if (isStandAloneSurvey && options.isStandAlonePreview === true) {
            // Standalone preview: no user id, no storage, no attributes and no
            // API queue. The <poll-hero> element fetches the survey (including
            // drafts) from the server and renders it as 'previewStandalone'.
            currentProjectId = projectId;
            currentIsStandAloneSurvey = true;
            currentIsStandAlonePreview = true;
            registerCustomElement();

            // The project config is only a fallback when the server fetch fails
            try {
                const data = await loadProjectConfig(projectId);
                loadedProjectConfig = data;
                currentCan = normalizeCan(data.can);
            } catch {
                // A project with only drafts may not have a published config
            }
            return;
        }

        if (surveyIdFromURL) {
            const previewSurvey = await fetchSurveyPreview(CONFIG.apiUrl, surveyIdFromURL);
            const previewSurveyType = getPreviewSurveyType(previewSurvey);
            
            const urlParams = new URLSearchParams(window.location.search);
            const considerTargeting = urlParams.get('pollhero-targeting') === 'true';

            if (considerTargeting) {
                const data = await loadProjectConfig(projectId);
                loadedProjectConfig = data;

                if (storage === null) {
                    handleCookieAndLocalStorage(data.storage_type, data.cookies_expiration_time);
                }

                fetchAndStoreUTMParameters(storage);

                if (!storage.get('ph_user_id')) {
                    let newUserId = generateUUID();
                    storage.set('ph_user_id', newUserId);
                }

                initializePH1API(storage, projectId);

                // A previewed survey may be paused or inactive, so its own flag
                // takes precedence over the project-level one
                attributes = await loadUserAttributes(
                    projectId,
                    previewSurvey.needs_user_attributes ?? data.needs_user_attributes,
                );

                const can = normalizeCan(data.can);
                const surveySettings = previewSurvey.survey_settings || surveySettingsDefaults;

                if (!checkURL(previewSurvey.rules, surveySettings.urlRulesLogicOperator, attributes)) return;

                checkShowTiming(surveySettings, previewSurvey, storage, can, previewSurveyType);
            } else {
                new SurveyEngine(previewSurvey, previewSurveyType, 0, null, null, normalizeCan(previewSurvey.can));
            }
            return;
        }

        const data = await loadProjectConfig(projectId);
        loadedProjectConfig = data;

        if (storage === null) {
            handleCookieAndLocalStorage(data.storage_type, data.cookies_expiration_time);
        }

        fetchAndStoreUTMParameters(storage);
        
        if (!storage.get('ph_user_id')) {
            let newUserId = generateUUID();
            storage.set('ph_user_id', newUserId);
        }

        initializePH1API(storage, projectId);

        attributes = await loadUserAttributes(projectId, data.needs_user_attributes);

        // <poll-hero> elements evaluate their rules in connectedCallback, so
        // the element is only defined once the attributes are available
        registerCustomElement();

        const can = normalizeCan(data.can);
        currentProjectId = projectId;
        currentIsStandAloneSurvey = isStandAloneSurvey;
        currentCan = can;

        processSurveys(data.surveys, projectId, isStandAloneSurvey, can);

        const handleQueue = setupPHAPIQueue(storage, projectId);
        handleQueue();
    } catch (error) {
        // Only visible in the dev build: the production build strips console calls
        console.warn('[PollHero] initialization failed:', error);
    }
}

// Selector and position stay in the saved survey configuration; the preview
// URL only needs to identify the survey and point at the desired host page.
function getPreviewSurveyType(survey) {
    const selectorInjectionEnabled =
        (survey.injection_settings?.enableCSSSelector === true ||
            survey.injection_settings?.enableCSSSelector === '1' ||
            survey.injection_settings?.enableCSSSelector === 1) && survey.injection_settings?.cssSelector != null;

    return selectorInjectionEnabled ? 'previewCssSelectorEmbed' : 'previewLink';
}

export function processSurveys(surveys, projectId, isStandAloneSurvey, can = {}) {
    const scriptTriggeredSurveys = [];
    const urlTriggeredSurveys = [];
    const embedSurveys = [];


    surveys.forEach((survey) => {
        if (!isSurveyActive(survey)) return;

        const isJsTriggered =
            survey.survey_settings?.isJsTargetingEnabled === true ||
            survey.survey_settings?.isJsTargetingEnabled === '1';

        if (isJsTriggered) {
            scriptTriggeredSurveys.push(survey);
        } else if (survey.type === 'onsite') {
            urlTriggeredSurveys.push(survey);
        } else if (survey.type === 'embed') {
            if (survey.injection_settings?.enableCSSSelector === true || survey.injection_settings?.enableCSSSelector === '1' || survey.injection_settings?.enableCSSSelector === 1) {
                embedSurveys.push(survey);
            }
        }
    });

    if (scriptTriggeredSurveys.length > 0 && !isStandAloneSurvey) {
        handleScriptTriggeredSurveys(scriptTriggeredSurveys, can);
    }

    if (urlTriggeredSurveys.length > 0 && !isStandAloneSurvey) {
        handleUrlTriggeredSurveys(urlTriggeredSurveys, can);
    }

    if (embedSurveys.length > 0 && !isStandAloneSurvey) {
        handleCssSelectorEmbedSurveys(embedSurveys, can);
    }

}

function handleScriptTriggeredSurveys(scriptTriggeredSurveys, can) {
    window.PH1 = {
        ...window.PH1,
        showSurvey: function (id) {
            const scriptTriggeredSurvey = scriptTriggeredSurveys.find((s) => s.survey_id === id);
            if (scriptTriggeredSurvey) {
                let survey = scriptTriggeredSurvey;
                if (!getSurveyDisplayDecision(survey, storage)) return;

                if (scriptTriggeredSurvey.questions.length) {
                    let surveySettings = scriptTriggeredSurvey.survey_settings || surveySettingsDefaults;
                    if (!checkShowConditions(surveySettings, survey.survey_id, storage)) return;
                    launchSurvey(survey, 'onsite', storage, can);
                }
            }
        },
    };
}

function handleUrlTriggeredSurveys(surveysTriggeredByURL, can) {
    surveysTriggeredByURL.some(function (survey) {
        let surveySettings = survey.survey_settings || surveySettingsDefaults;
        if (!getSurveyDisplayDecision(survey, storage)) return;
        if (!checkURL(survey.rules, survey.survey_settings?.urlRulesLogicOperator, attributes)) return;
        if (!checkShowConditions(surveySettings, survey.survey_id, storage)) return;
        checkShowTiming(surveySettings, survey, storage, can);
    });
}

function handleCssSelectorEmbedSurveys(embedSurveys, can) {
    embedSurveys.forEach(function (survey) {
        let surveySettings = survey.survey_settings || surveySettingsDefaults;
        if (!getSurveyDisplayDecision(survey, storage)) return;
        if (!checkURL(survey.rules, survey.survey_settings?.urlRulesLogicOperator, attributes)) return;
        if (!checkShowConditions(surveySettings, survey.survey_id, storage)) return;
        // One broken survey must not prevent the remaining embeds from
        // rendering: launchSurvey() catches and logs engine errors itself
        launchSurvey(survey, 'cssSelectorEmbed', storage, can);
    });
}


export function handleCookieAndLocalStorage(type, expirationDays) {
    storage = new StorageManager(type, expirationDays);
}

// `options.purchase` = a purchase tracked on this page ({ value, coupon, items }).
// It is applied to the attributes locally before they are reloaded, because the
// backend may not have aggregated the order yet; the server answer and the
// local copy are then merged (see display/attributes.js).
export async function refreshUrlTriggeredSurveys(options = {}) {
    if (!currentProjectId || !loadedProjectConfig) return;

    const local = options.purchase ? applyPurchaseToAttributes(attributes, options.purchase) : null;
    const fresh = await loadUserAttributes(currentProjectId, loadedProjectConfig.needs_user_attributes);
    attributes = local ? mergeAttributes(fresh, local) : fresh;

    if (local) {
        // Persist the merged attributes so a page reload within the cache
        // lifetime (30 min) still sees the purchase; afterwards the server
        // answer alone counts, by then the order is aggregated.
        storeUserAttributesCache(currentProjectId, storage?.get('ph_user_id'), attributes);
    }

    const urlTriggeredSurveys = [];

    loadedProjectConfig.surveys.forEach((survey) => {
        if (!isSurveyActive(survey)) return;

        const isJsTriggered =
            survey.survey_settings?.isJsTargetingEnabled === true ||
            survey.survey_settings?.isJsTargetingEnabled === '1';

        if (!isJsTriggered && survey.type === 'onsite') {
            urlTriggeredSurveys.push(survey);
        }
    });

    if (urlTriggeredSurveys.length > 0 && !currentIsStandAloneSurvey) {
        handleUrlTriggeredSurveys(urlTriggeredSurveys, currentCan);
    }
}
